import angular from 'angular';

const sortableClass = 'column-sortable';
const rowPanelElem = 'akam-table-row-panel';
const tableColumnElem = 'akam-table-column';
const checkBoxTemplate =
  `<th class="column-checkbox"
    translate
    translate-attr-title="components.data-table.tristate-checkbox.title"><span><i
    ng-click="table.changeVisibleItemsState()"
    ng-class="table.triStateSelectState"></i></span></th>`;

function TableTemplateService($log) {

  function getHeaderTemplate(element, attributes, selectable) {
    let template = '', headerHtml;

    // add select all checkbox
    if (selectable) {
      template += checkBoxTemplate;
    }

    if (!element || element.children.length === 0) {
      $log.warn('Expected "akam-table-column" tag found nothing');
    } else {
      angular.forEach(element.children, (elem) => {
        // clone the element because we will be modifying it with setAttribute and classList
        const elemClone = angular.element(elem).clone()[0];

        if (!isElementValid(elem)) {
          $log.warn('Expected "akam-table-column" or "akam-table-row-panel" tag, found',
            elemClone.nodeName.toLowerCase());
          return;
        }

        // set up ng-click and ng-class for handling sorting
        if (isSortable(elemClone, attributes)) {
          elemClone.classList.add(sortableClass);

          elemClone.setAttribute('ng-class',
            `table.sortDirectionClass("${elemClone.getAttribute('row-property')}")`);

          elemClone.setAttribute('ng-click',
            `table.sortColumn("${elemClone.getAttribute('row-property')}")`);
        }

        // akam-text-overflow expects a fully translated string already, so if the user
        // defines an akam-text-overflow for a table row, we will replace it with a title tag
        // with the translated value (using the translate filter). This is a trade-off
        // between adding unnecessary watches for every <th> item and delaying compiling this
        // element until we can translate the table header string.
        if (elemClone.hasAttribute('akam-text-overflow')) {
          elemClone.removeAttribute('akam-text-overflow');

          elemClone.setAttribute('title',
            `{{ \'${elemClone.getAttribute('header-name')}\' | translate}}`);
        }

        headerHtml = `<div class="th-wrapper">
                        <span translate="${elemClone.getAttribute('header-name')}"></span>
                        <i ng-class="{
                          'aci-sort-arrow-bottom': !table.isAscending(),
                          'aci-sort-arrow-top': table.isAscending()}"></i>
                      </div>`;

        let tpl = elemClone.outerHTML.replace('<akam-table-column', '<th');

        tpl = tpl.replace('akam-table-column>', 'th>');
        tpl = angular.element(tpl).html(headerHtml)[0].outerHTML;
        if (!isRowPanel(elem)) {
          template += tpl;
        }
      });
    }
    return template;
  }

  /**
   * Configures the template for the table row, including `td` elements.
   * @param {HTMLElement} element the `akam-table-row` element to compile.
   * @param {Object} attributes attributes given to the `akam-table` directive.
   * @param {Boolean} selectable Whether or not to add checkbox columns
   * @returns {String} the template string to compile for the header
   */
  function getRowTemplate(element, attributes, selectable) {
    let template = '';
    let isExpandable = false;

    if (selectable) {
      template += `
        <td class="column-checkbox">
          <label>
            <input type="checkbox" ng-model="table.selectedItemsBooleanMap[table.idPropertyFn(row)]"
              ng-change="table.toggleSelected(row)">
            <span class="label"></span>
          </label>
        </td>`;
    }

    if (!element || element.children.length === 0) {
      $log.warn('Expected "akam-table-row" tag found nothing');
    } else {
      angular.forEach(element.children, (elem) => {
        let content;

        if (!isElementValid(elem)) {
          $log.warn('Expected "akam-table-column" or "akam-table-row-panel" tag, found',
            elem.nodeName.toLowerCase());
          return;
        }

        // a monstrosity that is used to determine if the row is sortable or filterable and a row
        // property isn't defined.
        if (!elem.hasAttribute('row-property') &&
          (!angular.isDefined(attributes.notSortable) && !elem.hasAttribute('not-sortable') ||
            !angular.isDefined(attributes.notFilterable) && !elem.hasAttribute('not-filterable'))) {

          $log.debug('', elem, ' has no "row-property" attribute defined. The column will' +
            'be neither filterable nor sortable. Add "not-filterable" and "not-sortable"' +
            'to suppress this message.');
        }

        if (isRowPanel(elem)) {
          isExpandable = true;
        }

        let tpl = elem.outerHTML.replace('<akam-table-column', '<td');
        tpl = tpl.replace('akam-table-column>', 'td>');

        if (elem.innerHTML.trim()) {
          content = elem.innerHTML;
        } else {
          content = `{{ row.${elem.getAttribute('row-property')}}}`;
        }

        tpl = angular.element(tpl).html(content)[0].outerHTML;

        if (!isRowPanel(elem)) {
          template += tpl;
        }
      });
    }

    //if applicable provide first column with arrow button to expand row
    if (isExpandable) {
      template = makeFirstColumnExpandable(template, selectable);
    }
    return template;
  }

  /**
   * Makes a copy of all `akam-table-row` attributes
   * @param {HTMLElement} element the `akam-table-row` element to compile.
   * @returns {String} string containing all attributes copied from `akam-table-row`
   */
  function getRowAttributes(element) {
    let attributesString = '';

    if (!element || element.children.length === 0) {
      $log.warn('Expected "akam-table-row" tag found nothing');
    } else if (element.attributes && element.attributes.length > 0) {
      angular.forEach(element.attributes, (attr) => {
        attributesString += ` ${attr.name}="${attr.value}"`;
      });
    }

    return attributesString;
  }

  /**
   * Returns custom markup for expandable row panel
   * @param {HTMLElement} element the `akam-table-row` element to compile.
   * @param {Boolean} selectable Whether or not to add checkbox columns
   * @returns {String} string containing custom markup that will be rendered inside the panel
   */
  function getRowPanel(element, selectable) {
    let tpl = '';
    if (!element || element.children.length === 0) {
      $log.warn('Expected "akam-table-row" tag found nothing');
    } else {
      angular.forEach(element.children, (elem) => {
        if (elem.nodeName.toLowerCase() === rowPanelElem) {
          const colspan = selectable ? element.children.length : element.children.length - 1;
          tpl += `<td colspan="${colspan}"><div>${elem.innerHTML}</div></td>`;
        }
      });
    }
    return tpl;
  }

  function isSortable(element, attributes) {
    return !angular.isDefined(attributes.notSortable) && !element.hasAttribute('not-sortable') &&
      element.hasAttribute('row-property');
  }

  function makeFirstColumnExpandable(template, selectable) {
    const columnsArray = template.split(`<\/td>`);
    const index = selectable ? 1 : 0;
    let newTemplate;
    const arrowBtnTpl = `<i ng-class="{'aci-arrow-right': !row.isExpanded,
              'aci-arrow-bottom': row.isExpanded}" class="expand-button"
              ng-click="table.toggleRowPanel(row)"></i>`;
    const firstColumn = columnsArray[index].split('>');
    firstColumn[1] = arrowBtnTpl + firstColumn[1];
    columnsArray[index] = firstColumn.join('>');
    newTemplate = columnsArray.join(`<\/td>`);
    return newTemplate;
  }

  function isElementValid(elem) {
    return elem.nodeName.toLowerCase() === tableColumnElem
      || isRowPanel(elem);
  }

  function isRowPanel(elem) {
    return elem.nodeName.toLowerCase() === rowPanelElem;
  }

  return {
    template(element, attributes, selectable) {
      return `<table class="table table-striped table-hover">
              <thead><tr>${getHeaderTemplate(element[0], attributes, selectable)}</tr></thead>
              <tbody>
                  <tr ng-repeat-start="row in table.filtered track by table.idPropertyFn(row)"
                   ng-class="table.getRowClasses(table.idPropertyFn(row), $index+1)"
                   id="{{ ::table.id }}-{{::table.idPropertyFn(row)}}-row"
                      ${getRowAttributes(element[0])}>
                      ${getRowTemplate(element[0], attributes, selectable)}
                  </tr>
                  <tr ng-repeat-end ng-if="row.isExpanded" class="row-panel"
                  id="{{ ::table.id }}-{{::table.idPropertyFn(row)}}-row-panel"
                  ng-mouseenter="table.addHoverPrevious($event)"
                  ng-mouseleave="table.removeHoverPrevious($event)"
                  ng-class="table.getRowClasses(table.idPropertyFn(row), $index+1)">
                      ${getRowPanel(element[0], selectable)}
                  </tr>
              </tbody>
            </table>`;
    }
  };

}

TableTemplateService.$inject = ['$log'];

export default TableTemplateService;
